import { useCallback, useEffect, useMemo, useState } from "react";
import { InlineAssistantLayout } from "@/components/assistant/InlineAssistantLayout";
import { DraftClauseLibraryAssistantPanel } from "../components/draft-clause-library-assistant/DraftClauseLibraryAssistantPanel";
import { Layers3, Pencil, Plus, Trash2 } from "lucide-react";
import { ActionOverflowMenu, ActionOverflowMenuItem } from "@doculegis/shared";
import {
  DraftClauseSheet,
  type DraftClauseSheetDraft,
} from "@/components/draft-clauses/DraftClauseSheet";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { GlassCard } from "@/components/common/GlassCard";
import { PageMobileActions } from "@/components/common/PageMobileActions";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/contexts/ToastContext";
import { usePermission } from "@/contexts/AuthContext";
import {
  deleteDraftClauseAlternative,
  fetchDraftClauseLibrary,
  fetchDraftTemplates,
} from "@/lib/api/drafts";
import {
  formatClauseCategoryLabel,
  formatClauseVariantLabel,
} from "@/lib/clause-variant-labels";
import { parseTemplateSections } from "@/lib/draft-template-utils";
import { cn } from "@/lib/utils";
import {
  pageActionButtonClassName,
  pageActionDesktopOnlyClassName,
  pageActionLabelClassName,
} from "@/lib/page-action-button";
import type { DraftClauseAlternative, DraftTemplate } from "@/types/drafting";

export function DraftClauseLibraryPage() {
  const { showToast } = useToast();
  const canCreate = usePermission("draftClause", "create");
  const canUpdate = usePermission("draftClause", "update");
  const canDelete = usePermission("draftClause", "delete");
  const showAssistant = canCreate || canUpdate;

  const [templates, setTemplates] = useState<DraftTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [clauses, setClauses] = useState<DraftClauseAlternative[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [isLoadingClauses, setIsLoadingClauses] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetDraft, setSheetDraft] = useState<DraftClauseSheetDraft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DraftClauseAlternative | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) ?? null,
    [templates, selectedTemplateId]
  );

  const templateSections = useMemo(
    () => parseTemplateSections(selectedTemplate?.structure),
    [selectedTemplate?.structure]
  );

  const sectionTitleByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const section of templateSections) {
      map.set(section.key, section.title);
    }
    return map;
  }, [templateSections]);

  const groupedClauses = useMemo(() => {
    const map = new Map<string, DraftClauseAlternative[]>();
    for (const clause of clauses) {
      const key = clause.section_key;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(clause);
    }
    return map;
  }, [clauses]);

  async function loadTemplates() {
    setIsLoadingTemplates(true);
    setError(null);
    try {
      const rows = await fetchDraftTemplates();
      setTemplates(rows);
      if (!selectedTemplateId && rows.length) {
        setSelectedTemplateId(rows[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load templates");
    } finally {
      setIsLoadingTemplates(false);
    }
  }

  const loadClauses = useCallback(async (templateId: string, options?: { background?: boolean }) => {
    if (!templateId) {
      setClauses([]);
      return;
    }
    const background = options?.background === true;
    if (!background) {
      setIsLoadingClauses(true);
    }
    setError(null);
    try {
      const rows = await fetchDraftClauseLibrary(templateId);
      setClauses(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load clause library");
    } finally {
      if (!background) {
        setIsLoadingClauses(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadTemplates();
  }, []);

  useEffect(() => {
    if (selectedTemplateId) void loadClauses(selectedTemplateId);
  }, [selectedTemplateId]);

  function openCreate(sectionKey?: string) {
    if (!selectedTemplateId) return;
    setSheetDraft({
      mode: "create",
      template_id: selectedTemplateId,
      section_key: sectionKey ?? templateSections[0]?.key ?? "",
      clause_category: "",
      language: "",
      posture: "",
      jurisdiction_applicability: "general",
      approval_status: "approved",
      version: "v1.0",
    });
    setSheetOpen(true);
  }

  function openCustomize(clause: DraftClauseAlternative) {
    setSheetDraft({
      mode: "customize",
      id: clause.id,
      template_id: clause.template_id,
      section_key: clause.section_key,
      clause_category: clause.clause_category ?? "",
      language: clause.language,
      posture: (clause.posture as DraftClauseSheetDraft["posture"]) ?? "",
      jurisdiction_applicability: clause.jurisdiction_applicability ?? "general",
      approval_status: clause.approval_status ?? "approved",
      version: clause.version ?? "v1.0",
    });
    setSheetOpen(true);
  }

  function openEdit(clause: DraftClauseAlternative) {
    if (clause.is_system) {
      openCustomize(clause);
      return;
    }

    setSheetDraft({
      mode: "edit",
      id: clause.id,
      template_id: clause.template_id,
      section_key: clause.section_key,
      clause_category: clause.clause_category ?? "",
      language: clause.language,
      posture: (clause.posture as DraftClauseSheetDraft["posture"]) ?? "",
      jurisdiction_applicability: clause.jurisdiction_applicability ?? "general",
      approval_status: clause.approval_status ?? "approved",
      version: clause.version ?? "v1.0",
    });
    setSheetOpen(true);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDraftClauseAlternative(deleteTarget.id);
      showToast("Clause variant deleted.", "success");
      setDeleteTarget(null);
      await loadClauses(selectedTemplateId);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to delete clause variant", "error");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <section
      className={cn(
        "relative p-6 md:p-8",
        !assistantOpen && "pb-24 sm:pb-8",
        assistantOpen && "flex min-h-0 flex-1 flex-col overflow-hidden"
      )}
    >
      <div className="mb-5 flex shrink-0 flex-row items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-[var(--primary-soft-border)] bg-[var(--primary-soft-bg)] text-[#7c3aed] dark:border-violet-300/30 dark:bg-violet-500/15 dark:text-violet-200">
            <Layers3 className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
              Clause Library
            </h1>
            <p className="hidden text-sm text-[var(--lm-muted)] sm:block dark:text-slate-300/85">
              Manage firm-approved clause variants by template and section for your organization.
            </p>
          </div>
        </div>
        {canCreate && selectedTemplateId ? (
          <Button
            type="button"
            onClick={() => openCreate()}
            className={`${pageActionButtonClassName} ${pageActionDesktopOnlyClassName}`}
          >
            <Plus className="h-4 w-4" />
            <span className={pageActionLabelClassName}>New variant</span>
          </Button>
        ) : null}
      </div>

      {canCreate && selectedTemplateId ? (
        <PageMobileActions
          actions={[
            {
              id: "new-clause",
              icon: Plus,
              label: "New variant",
              onClick: () => openCreate(),
            },
          ]}
        />
      ) : null}

      <InlineAssistantLayout
        open={assistantOpen}
        onOpenChange={setAssistantOpen}
        showTrigger={showAssistant}
        triggerLabel="Clause Library Assistant"
        assistant={
          <DraftClauseLibraryAssistantPanel
            key={selectedTemplateId || "no-template"}
            className="h-full min-h-0"
            selectedTemplateId={selectedTemplateId}
            selectedTemplateName={selectedTemplate?.name}
            templateSections={templateSections}
            clauses={clauses}
            onRefresh={async (options) => {
              if (selectedTemplateId) {
                await loadClauses(selectedTemplateId, options);
              }
            }}
            onClose={() => setAssistantOpen(false)}
          />
        }
      >
      <GlassCard className="mb-4 border-[var(--lm-border-card)] bg-white p-4 shadow-[var(--shadow-card)] dark:border-glass-border dark:bg-slate-950/45 dark:shadow-none">
        <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-400">
          Draft template
        </label>
        <Select
          value={selectedTemplateId || undefined}
          onValueChange={setSelectedTemplateId}
          disabled={isLoadingTemplates || !templates.length}
        >
          <SelectTrigger className="max-w-xl">
            <SelectValue placeholder="Select draft template" />
          </SelectTrigger>
          <SelectContent>
            {templates.map((template) => (
              <SelectItem key={template.id} value={template.id}>
                {template.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="mt-2 text-xs text-[var(--lm-muted)] dark:text-slate-400">
          Variants are scoped to a template section. Use <strong>Customize for my organization</strong> on
          system defaults to save your own wording without affecting other tenants. Drafters see the
          effective variants in the workspace Clauses tab.
        </p>
      </GlassCard>

      <GlassCard className="overflow-hidden border-[var(--lm-border-card)] bg-white p-0 shadow-[var(--shadow-card)] dark:border-glass-border dark:bg-slate-950/45 dark:shadow-none">
        <h2 className="border-b border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] px-4 py-3 text-[11px] font-medium uppercase tracking-wide text-[var(--lm-muted)] dark:border-white/10 dark:bg-transparent dark:text-slate-400">
          Clause variants
        </h2>

        {isLoadingClauses ? (
          <p className="px-4 py-3.5 text-sm text-[var(--lm-muted)]">Loading clause library…</p>
        ) : null}
        {error ? <p className="px-4 py-3.5 text-sm text-red-300">{error}</p> : null}

        {!isLoadingClauses && !clauses.length ? (
          <p className="px-4 py-3.5 text-sm text-[var(--lm-muted)] dark:text-slate-400">
            No clause variants for this template yet. Add your organization&apos;s approved wording.
          </p>
        ) : null}

        <div className="divide-y divide-[var(--lm-border-subtle)] dark:divide-white/10">
          {Array.from(groupedClauses.entries()).map(([sectionKey, sectionClauses]) => (
            <div key={sectionKey} className="px-4 py-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
                  {sectionTitleByKey.get(sectionKey) ?? sectionKey}
                </h3>
                {canCreate ? (
                  <Button type="button" size="xs" variant="subtle" onClick={() => openCreate(sectionKey)}>
                    <Plus className="h-3.5 w-3.5" />
                    Add variant
                  </Button>
                ) : null}
              </div>

              <div className="space-y-2">
                {sectionClauses.map((clause) => {
                  const isSystem = Boolean(clause.is_system);
                  const label = formatClauseVariantLabel(clause.posture, clause.clause_category);
                  const category = clause.clause_category
                    ? formatClauseCategoryLabel(clause.clause_category)
                    : null;

                  return (
                    <div
                      key={clause.id}
                      className="flex items-start justify-between gap-3 rounded-lg border border-[var(--lm-border-subtle)] px-3 py-2.5 dark:border-slate-700"
                    >
                      <div className="min-w-0">
                        <div className="mb-1 flex flex-wrap items-center gap-2">
                          <p className="text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-slate-100">
                            {label}
                          </p>
                          {category ? (
                            <span className="rounded-full bg-[var(--surface-sunken)] px-2 py-0.5 text-[10px] text-[var(--lm-muted)] dark:bg-slate-800 dark:text-slate-400">
                              {category}
                            </span>
                          ) : null}
                          {isSystem ? (
                            <span className="rounded-full bg-[var(--tint-purple-bg)] px-2 py-0.5 text-[10px] font-medium text-[var(--tint-purple-fg)] dark:bg-indigo-500/15 dark:text-indigo-200">
                              System
                            </span>
                          ) : (
                            <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-300">
                              Organization
                            </span>
                          )}
                        </div>
                        <p className="line-clamp-2 text-xs text-[var(--lm-muted)] dark:text-slate-400">
                          {clause.language}
                        </p>
                      </div>

                      <ActionOverflowMenu>
                        {canUpdate ? (
                          <ActionOverflowMenuItem
                            icon={Pencil}
                            label={isSystem ? "Customize for my organization" : "Edit"}
                            onClick={() => openEdit(clause)}
                          />
                        ) : null}
                        {!isSystem && canDelete ? (
                          <ActionOverflowMenuItem
                            icon={Trash2}
                            label="Delete"
                            variant="destructive"
                            onClick={() => setDeleteTarget(clause)}
                          />
                        ) : null}
                      </ActionOverflowMenu>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </GlassCard>
      </InlineAssistantLayout>

      <DraftClauseSheet
        open={sheetOpen}
        onOpenChange={(open) => {
          setSheetOpen(open);
          if (!open) setSheetDraft(null);
        }}
        draft={sheetDraft}
        sections={templateSections}
        onSaved={() => {
          showToast(
            sheetDraft?.mode === "customize"
              ? "Organization clause variant saved."
              : sheetDraft?.mode === "edit"
                ? "Clause variant updated."
                : "Clause variant added.",
            "success"
          );
          void loadClauses(selectedTemplateId);
        }}
      />

      <ConfirmDialog
        isOpen={deleteTarget != null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete clause variant"
        description="Delete this organization clause variant? The system default will appear again for drafters."
        confirmLabel="Delete"
        variant="destructive"
        isLoading={deleting}
      />
    </section>
  );
}
